import React from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Zap, Coins } from "lucide-react";

/**
 * Reusable "+XP Earned" micro-interaction — fired on any task completion
 * (challenge day, quiz pass, case study, boss battle, interview, duel, etc.)
 * Renders a Framer Motion floating/glowing badge via sonner's toast.custom
 * so it stacks correctly with other toasts instead of blocking a modal.
 */
function XPToastCard({ xp, coins, label }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.85 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 320, damping: 20 }}
      data-testid="xp-earned-toast"
      className="flex items-center gap-3 bg-gradient-to-r from-[#1a2333] to-[#131a29] border border-[#FBBF24]/30 rounded-2xl px-4 py-3 shadow-[0_8px_30px_rgba(251,191,36,0.15)] min-w-[220px]"
    >
      <motion.div
        animate={{ rotate: [0, -12, 12, 0], scale: [1, 1.15, 1] }}
        transition={{ duration: 0.6, ease: "easeInOut" }}
        className="w-9 h-9 rounded-xl bg-[#FBBF24]/15 flex items-center justify-center shrink-0"
      >
        <Zap className="w-5 h-5 text-[#FBBF24]" />
      </motion.div>
      <div className="flex-1">
        <p className="text-xs text-slate-400 font-medium">{label || "Task Complete!"}</p>
        <div className="flex items-center gap-3 mt-0.5">
          <motion.span
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            data-testid="xp-earned-amount"
            className="text-base font-heading font-bold text-[#FBBF24]"
          >
            +{xp} XP
          </motion.span>
          {!!coins && (
            <motion.span
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              data-testid="xp-earned-coins"
              className="flex items-center gap-1 text-sm font-semibold text-[#5b9dff]"
            >
              <Coins className="w-3.5 h-3.5" /> +{coins}
            </motion.span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/**
 * showXPToast(xp, coins?, label?) — call this instead of a plain toast.success
 * whenever the backend confirms an XP-earning action succeeded.
 */
export function showXPToast(xp, coins = 0, label) {
  if (!xp && !coins) return;
  toast.custom(() => <XPToastCard xp={xp} coins={coins} label={label} />, {
    duration: 3200,
  });
}
